const storageKey = (buildingId: number) => `races-channel-order:${buildingId}`

export interface OrderedChannel {
  id: number | string
  name?: string
}

export function readChannelOrder(buildingId: number): string[] {
  try {
    const raw = localStorage.getItem(storageKey(buildingId))
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.flatMap((item) => {
      if (typeof item === 'string' || typeof item === 'number') return [String(item)]
      return []
    })
  } catch {
    return []
  }
}

export function writeChannelOrder(buildingId: number, ids: readonly string[]) {
  localStorage.setItem(storageKey(buildingId), JSON.stringify(ids))
}

export function orderChannels<T extends OrderedChannel>(
  channels: readonly T[],
  savedIds: readonly string[],
): T[] {
  const byId = new Map(channels.map((channel) => [String(channel.id), channel]))
  const ordered: T[] = []
  const used = new Set<string>()

  for (const id of savedIds) {
    const channel = byId.get(id)
    if (!channel || used.has(id)) continue
    ordered.push(channel)
    used.add(id)
  }

  for (const channel of channels) {
    const id = String(channel.id)
    if (used.has(id)) continue
    ordered.push(channel)
    used.add(id)
  }

  return ordered
}

function isOrderedChannel(value: unknown): value is OrderedChannel {
  if (typeof value !== 'object' || value === null || !('id' in value)) return false
  const id = (value as { id: unknown }).id
  return typeof id === 'number' || typeof id === 'string'
}

export function toOrderedChannels(data: unknown): OrderedChannel[] {
  if (!Array.isArray(data)) return []
  return data.filter(isOrderedChannel)
}
